import {
    Typography,
    Stack,
    InputAdornment,
    Divider,
    Chip,
    CardHeader
} from '@mui/material'

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation, Trans } from 'react-i18next';

import { FormProvider, RHFTextField } from './hook-form';
import IRPCalculationDetail from './IRPCalculationDetail';
import { Fragment, useEffect, useState } from 'react';

import { AMOUNT_LOCALE } from '../i18n';

//
// Paraguayan minimum wage -- only seeds the form's default values, it is not
// part of the tax calculation. Updated by hand when the government revises it.
// Gs 3.044.000 since 2026-07-01 (Decreto 6225/2026, Resolucion MTESS 670/2026).
//
const PY_MIN_WAGE             = 3044000;
const PYG_SYMBOL              = 'PY₲';
const _millions               = hundreds => hundreds * 1000000;
//
// IRP - Rentas por Servicios Personales, per Ley 6380/2019 and the DNIT
// cartilla: taxpayer above Gs 80.000.000 of gross annual income, then a
// progressive scale of 8% up to 50M, 9% up to 150M, 10% above.
// Verified against DNIT as current on 2026-08-15 -- unchanged since 2019.
//
const YEARLY_INCOME_THRESHOLD = _millions(80);

const formatAmount = value => `${PYG_SYMBOL} ${Intl.NumberFormat(AMOUNT_LOCALE).format(value)}`;

//
// Keeps the original <i><b>..</b></i> emphasis when injected by <Trans/>.
//
const Emphasis = ({ children }) => <i><b>{children}</b></i>;

const BracketPercentages = ({ brackets = [] }) => {
    const formatter = Intl.NumberFormat(AMOUNT_LOCALE);

    return (
      <>
        {brackets.map(bracket => (
          <Fragment key={bracket.percent_tax}>
            <Chip label={formatter.format(bracket.percent_tax) + '%'}/> {', '}{' '}
          </Fragment>
        ))}
      </>
    );
};

//
// Renders a result descriptor produced by computeResult(). Kept separate from the
// calculation itself so switching language re-renders the sentence rather than
// leaving stale JSX frozen in state.
//
function ResultMessage({ result }) {
    const { t } = useTranslation();

    if (!result) return null;
    if (result.status === 'empty') return <>{t('result.empty')}</>;

    const threshold = <Chip label={formatAmount(YEARLY_INCOME_THRESHOLD)}/>;

    if (result.status === 'below') {
      return <Trans i18nKey="result.below" components={{ threshold, em: <Emphasis/> }}/>;
    }

    const { netProfitAmount, taxes, noTaxesDue, nonPositive, brackets } = result;

    const sign = t(`result.sign.${netProfitAmount === 0 ? 'neutral' : (netProfitAmount > 0 ? 'positive' : 'negative')}`);

    const explainer = noTaxesDue
      ? <Trans i18nKey="result.noTaxesDue" components={{ em: <Emphasis/> }}/>
      : <Trans i18nKey="result.taxesDue"
               components={{ taxes: <Chip color="primary" label={formatAmount(taxes)}/> }}/>;

    if (nonPositive) {
      return (
        <>
          <Trans i18nKey="result.computedNonPositive"
                 values={{ sign }}
                 components={{ threshold, em: <em/>, net: <Chip color="success" label={formatAmount(netProfitAmount)}/> }}/>
          &nbsp;{explainer}.
        </>
      );
    }

    return (
      <>
        <Trans i18nKey="result.computed"
               values={{ sign, brackets: t('result.bracketCount', { count: brackets.length }) }}
               components={{
                 threshold,
                 em: <em/>,
                 net: <Chip color={ netProfitAmount < 0 ? "success" : "default" } label={formatAmount(netProfitAmount)}/>,
                 percentages: <BracketPercentages brackets={brackets}/>
               }}/>
        {explainer}.
      </>
    );
}

export default function IRPCalculatorForm() {
    const { t }                       = useTranslation();
    const [ result, setResult ]       = useState(null);

    const addDots                     = (num) => {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    };
    const removeNonNumeric            = num => num.toString().replace(/[^0-9]/g, "");

    const defaultValues = {
      monthlyIncome:    addDots(PY_MIN_WAGE), 
      yearlyIncome:     addDots(PY_MIN_WAGE * 12),
      monthlyExpenses:  '',
      yearlyExpenses:   ''
    };
  
    const amountValidator = Yup.number()
                                .min(0, t('validation.negative'))
                                .nullable(false)
                                .typeError(t('validation.notANumber'))
                                .required(t('validation.required'));

    const validationSchema = Yup.object().shape({
      monthlyIncome:    amountValidator,
      yearlyIncome:     amountValidator,
      monthlyExpenses:  amountValidator,
      yearlyExpenses:   amountValidator
    });

    const methods = useForm({
      resolver: yupResolver(validationSchema),
      defaultValues,
    });
  
    const {
      setValue,
      getValues,
      // formState: { errors, isSubmitting },
    } = methods;

    // Seed the result from the default values on mount only; every later
    // recalculation is driven by the field onChange handlers below.
    useEffect(() => {
      setResult(computeResult());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const calculateByTaxBracket = (taxableIncome) => {
      const byBracket           = [];
      let carryOver             = taxableIncome;
      let taxableBracketAmount  = taxableIncome;
      let bracketTaxes          = 0;  

      //
      // lower tax bracket: 8% on 0 - 50M when net income is above YEARLY_INCOME_THRESHOLD
      //
      if ( taxableIncome >= 0 ) {
        if ( taxableIncome > _millions(50) )
          taxableBracketAmount  = _millions(50)
        else
          taxableBracketAmount  = carryOver;
        
        bracketTaxes          = taxableBracketAmount;
        carryOver            -= bracketTaxes;

        byBracket.push({ amount: Math.floor(taxableBracketAmount * 0.08), percent_tax: 8, carry_over: carryOver})
      }
      
      //
      // mid tax bracket: 9% on 50M - 150M 
      //
      if ( taxableIncome >= (_millions(50) + 1) ) {
        if ( carryOver > _millions(150 - 50) )
          taxableBracketAmount  = _millions(150 - 50)
        else
          taxableBracketAmount  = carryOver
        
        bracketTaxes          = Math.floor(taxableBracketAmount * 0.09);
        carryOver             -= taxableBracketAmount;

        byBracket.push({ amount: bracketTaxes, percent_tax: 9, carry_over: carryOver })
      } 

      //
      // max tax bracket: 10% on 150M+
      //
      if ( taxableIncome >= _millions(150) + 1 ) {
        byBracket.push({ amount: Math.floor(carryOver * 0.1), percent_tax: 10, carry_over: carryOver })
      }

      return byBracket;
    };

    //
    // Returns a language-independent description of the outcome; ResultMessage
    // turns it into a sentence.
    //
    const computeResult = () => {
      const yearlyIncome    = removeNonNumeric(getValues("yearlyIncome"));
      const yearlyExpenses  = removeNonNumeric(getValues("yearlyExpenses"));
      
      const netProfitAmount = yearlyIncome - yearlyExpenses;

      if ( (yearlyIncome === 0 && yearlyExpenses === 0) || yearlyIncome === undefined || yearlyExpenses === undefined ) {
        return { status: 'empty' };
      }
      
      if ( yearlyIncome < YEARLY_INCOME_THRESHOLD ) {
        return { status: 'below' };
      }
      
      const taxesByBracket  = calculateByTaxBracket(netProfitAmount);
      const taxes           = taxesByBracket.reduce((total, bracket) => total + bracket.amount, 0);
      const noTaxesDue      = taxes === 0 && yearlyExpenses > 0;

      return {
        status:       'computed',
        netProfitAmount,
        taxes,
        noTaxesDue,
        nonPositive:  netProfitAmount <= 0,
        // the bracket breakdown table is only shown for a positive net income
        brackets:     netProfitAmount <= 0 ? null : taxesByBracket
      };
    }

    const setNumericValue = (fieldName, amount) => {
      setValue(fieldName, addDots(amount));
    }

    const onMonthlyIncomeChange = function (e) {

      const amount  = removeNonNumeric(e.target.value);

      setNumericValue("monthlyIncome", addDots(amount));
      setNumericValue("yearlyIncome", addDots(Math.ceil(amount * 12)));

      setResult(computeResult());
    }

    const onYearlyIncomeChange = e => {
      const amount  = removeNonNumeric(e.target.value);

      setNumericValue("yearlyIncome", addDots(amount));
      setNumericValue("monthlyIncome", addDots(Math.ceil(amount / 12)));

      setResult(computeResult());
    }

    const onMonthlyExpenseChange = function (e) {

      const amount  = removeNonNumeric(e.target.value);

      setNumericValue("monthlyExpenses", addDots(amount));
      setNumericValue("yearlyExpenses", addDots(Math.ceil(amount * 12)));

      setResult(computeResult());
    }

    const onYearlyExpenseChange = e => {
      const amount  = removeNonNumeric(e.target.value);

      setNumericValue("yearlyExpenses", addDots(amount));
      setNumericValue("monthlyExpenses", addDots(Math.ceil(amount / 12)));

      setResult(computeResult());
    }

    return (
      <FormProvider methods={methods}>
        
        <Stack spacing={3}>

          <CardHeader title={t('form.title')}/>

          <Divider sx={{ display: { xs: 'block', sm: 'none', md: 'none' } }}> ➕ <strong>{t('form.incomeDivider')}</strong> </Divider>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">

            <RHFTextField name="monthlyIncome" label={t('form.monthlyIncome')}
              onChange={onMonthlyIncomeChange}
              InputProps={{
                startAdornment: (<InputAdornment position="start">{PYG_SYMBOL}</InputAdornment>)}}/>
            
            <Typography> = </Typography>
            
            <RHFTextField name="yearlyIncome" label={t('form.yearlyIncome')}
              onChange={onYearlyIncomeChange}
              InputProps={{
                startAdornment: (<InputAdornment position="start">{PYG_SYMBOL}</InputAdornment>)}}/>

          </Stack>
          
          <Divider sx={{ display: { xs: 'block', sm: 'none', md: 'none' } }}> ➖ <strong>{t('form.expensesDivider')}</strong> </Divider>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            
            <RHFTextField name="monthlyExpenses" label={t('form.monthlyExpenses')}
              onChange={onMonthlyExpenseChange}
              InputProps={{
                startAdornment: (<InputAdornment position="start">{PYG_SYMBOL}</InputAdornment>)}}/>
            
            <Typography> = </Typography>

            <RHFTextField name="yearlyExpenses" label={t('form.yearlyExpenses')}
              onChange={onYearlyExpenseChange}
              InputProps={{
                startAdornment: (<InputAdornment position="start">{PYG_SYMBOL}</InputAdornment>)}}/>
          </Stack>

          <Divider />
                
          <Stack direction={{ xs: 'column', sm: 'column' }} spacing={0} alignItems="center">
            {/* component="div": the result sentence embeds <Chip/>, which renders
                a <div> and is invalid inside Typography's default <p> */}
            <Typography variant="body1" component="div">
              <ResultMessage result={result}/>
              <br />
              
            </Typography>

            {result?.brackets && <IRPCalculationDetail calculatedTaxes={result.brackets}/>}
          </Stack>
        </Stack>

        </FormProvider>

        
    );
}
